"use client";

import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import posthog from "posthog-js";
import { apiFetch, ApiClientError } from "@/lib/api/client";
import type { FeatureKey } from "@/lib/authz/policy";
import { deriveCurrentPlan } from "@/lib/authz/plans";
import type { AuthSessionPayload, IpLockStatus } from "@/lib/authz/types";
import type { UserRole } from "@/types/admin";
import { supabase } from "@/lib/supabaseClient";
import { identifyTikTokUser } from "@/lib/tracking/tiktokClient";

export type { FeatureKey } from "@/lib/authz/policy";
export type { IpLockStatus } from "@/lib/authz/types";

const defaultPermissions: Record<FeatureKey, boolean> = {
  paintbooth: false,
  eupviewer: false,
  addoneupcreator: false,
  weaponviewer: false,
  uvstudio: false,
  liverymagic: false,
  itemimage: false,
  badgemaker: false,
  embroiderytool: false,
  blinklab: false,
  blinklab3d: false,
  metaroom: false,
  packbench: false,
  codesmith: false,
  codevisualizer: false,
  scriptdocs: false,
  tripoai: false,
  chevronbuilder: false,
  creatorconvert: false,
  inklab: false,
  handlingdiff: false,
  resmon: false,
  assets: false,
};

const defaultIpLock: IpLockStatus = {
  checked: true,
  blocked: false,
  ipLockDisabled: true,
  dynamicIpMode: false,
  currentIp: null,
  lastLoginIp: null,
  lockedIp: null,
  canChangeIp: false,
  nextChangeAt: null,
  lastIpChangeAt: null,
  extraAllowedIps: [],
  deviceKnown: false,
  deviceHash: null,
};

const defaultBilling: AuthSessionPayload["billing"] = {
  personalSubscriptionId: null,
  personalSubscriptionIds: [],
  personalSubscriptionData: null,
};

type AuthContextValue = {
  user: AuthSessionPayload["user"];
  session: { user: NonNullable<AuthSessionPayload["user"]> } | null;
  loading: boolean;
  role: UserRole | null;
  isAdmin: boolean;
  canChangePlan: boolean;
  billing: AuthSessionPayload["billing"];
  permissions: AuthSessionPayload["permissions"];
  tokens: number;
  displayTokens: number;
  ipLock: IpLockStatus;
  sharingSuspended: boolean;
  sharingSuspendedReason: string | null;
  refreshPermissions: () => Promise<void>;
  refreshIpLock: () => Promise<void>;
  authorizeFeature: (feature: FeatureKey, options?: { forceRefresh?: boolean }) => Promise<boolean>;
  getKnownFeatureAccess: (feature: FeatureKey) => boolean | null;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function isTransientFetchError(error: unknown) {
  if (error instanceof DOMException) {
    return error.name === "AbortError";
  }
  if (error instanceof TypeError && /fetch/i.test(error.message)) {
    return true;
  }
  // Turbopack compiles routes on demand — a 404 on the first request after
  // dev server startup means the route hasn't been compiled yet, not that it's missing.
  if (process.env.NODE_ENV !== "production" && error instanceof ApiClientError && error.status === 404) {
    return true;
  }
  return false;
}

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function shouldDebugSeal() {
  if (typeof window === "undefined") return false;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get("debugSeal") === "1") return true;
    return window.localStorage.getItem("debug_seal_sync") === "1";
  } catch {
    return false;
  }
}

async function loadServerSession(options?: {
  forceSubscriptionRefresh?: boolean;
  retries?: number;
}) {
  const retries = options?.retries ?? 2;
  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await apiFetch<AuthSessionPayload>("/api/auth/session", {
        method: options?.forceSubscriptionRefresh ? "POST" : "GET",
      });
    } catch (error) {
      lastError = error;
      if (attempt >= retries || !isTransientFetchError(error)) {
        throw error;
      }
      await delay(500 * (attempt + 1));
    }
  }

  throw lastError;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [serverSession, setServerSession] = useState<AuthSessionPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const inFlightForcedRefreshRef = useRef<Promise<AuthSessionPayload | null> | null>(null);
  const lastForcedRefreshAtRef = useRef(0);
  const inFlightRefreshRef = useRef<Promise<AuthSessionPayload | null> | null>(null);
  const lastRefreshAtRef = useRef(0);
  const authRetryAtRef = useRef(0);
  const authRetryTimerRef = useRef<number | null>(null);
  const mountedRef = useRef(true);
  const serverSessionRef = useRef<AuthSessionPayload | null>(null);
  serverSessionRef.current = serverSession;

  const refresh = useCallback(async (options?: { forceSubscriptionRefresh?: boolean; background?: boolean }) => {
    if (Date.now() < authRetryAtRef.current) return serverSessionRef.current;
    if (inFlightRefreshRef.current) return inFlightRefreshRef.current;
    if (options?.background && Date.now() - lastRefreshAtRef.current < 15000) return serverSessionRef.current;
    if (options?.forceSubscriptionRefresh) {
      if (inFlightForcedRefreshRef.current) {
        return inFlightForcedRefreshRef.current;
      }

      const now = Date.now();
      if (now - lastForcedRefreshAtRef.current < 1500) {
        return serverSessionRef.current;
      }
    }

    const run = async () => {
    try {
      // Security note: permissions and account state are fetched from the backend
      // every time this refreshes. The browser never becomes the authority.
      const next = await loadServerSession(options);
      if (authRetryTimerRef.current !== null) {
        window.clearTimeout(authRetryTimerRef.current);
        authRetryTimerRef.current = null;
      }
      if (options?.forceSubscriptionRefresh && shouldDebugSeal()) {
        console.groupCollapsed("[seal-debug] forced subscription refresh");
        console.log("authenticated", next?.authenticated ?? false);
        console.log("userId", next?.user?.id ?? null);
        console.log("email", next?.user?.email ?? null);
        console.log("personalSubscriptionId", next?.billing?.personalSubscriptionId ?? null);
        console.log("personalSubscriptionIds", next?.billing?.personalSubscriptionIds ?? []);
        console.log("permissions", next?.permissions ?? null);
        console.log("personalSubscriptionData", next?.billing?.personalSubscriptionData ?? null);
        console.groupEnd();
      }
      serverSessionRef.current = next;
      setServerSession(next);
      // TikTok Advanced Matching. Attaches the signed-in identifiers to every
      // subsequent pixel event so client conversions join back to the same
      // person as the server Events API fire (which sends the SHA-256 of the
      // same email + user id). Kept outside the posthog.__loaded guard — the two
      // trackers load independently, and no-ops on its own when the pixel is
      // absent.
      if (next?.authenticated && next.user?.id) {
        void identifyTikTokUser({ email: next.user.email, externalId: next.user.id });
      }
      if (posthog.__loaded) {
        if (next?.authenticated && next.user?.id) {
          const planTier = deriveCurrentPlan(next.billing?.personalSubscriptionData);
          const hasSubscription = Boolean(next.billing?.personalSubscriptionId);
          const subscriptionCount = next.billing?.personalSubscriptionIds?.length ?? 0;
          const permissionsCount = Object.values(next.permissions ?? {}).filter(Boolean).length;
          const discordConnected = Boolean(next.user.user_metadata?.discord_user_id);

          // Rich person properties so PostHog cohorts/funnels can segment by
          // plan, role, credits, and Discord status — not just email.
          posthog.identify(next.user.id, {
            email: next.user.email,
            role: next.role,
            is_admin: next.isAdmin,
            account_status: next.accountStatus,
            plan_tier: planTier,
            has_subscription: hasSubscription,
            subscription_count: subscriptionCount,
            tokens: next.tokens,
            permissions_count: permissionsCount,
            discord_connected: discordConnected,
            discord_username: next.user.user_metadata?.discord_username ?? null,
          });
          // Super properties: attach plan/admin to EVERY event without a join.
          posthog.register({
            plan_tier: planTier,
            is_admin: next.isAdmin,
            has_subscription: hasSubscription,
          });
          // Group analytics by plan tier (account-level retention/adoption).
          posthog.group("plan", planTier, { tier: planTier, has_subscription: hasSubscription });
        } else if (!next?.authenticated) {
          posthog.reset();
        }
      }
      return next;
    } catch (error) {
      if (error instanceof ApiClientError && error.status === 429) {
        const cooldownMs = Math.max(60, Number(error.payload?.retryAfter) || 60) * 1000;
        authRetryAtRef.current = Date.now() + cooldownMs;
        if (mountedRef.current) {
          if (authRetryTimerRef.current !== null) window.clearTimeout(authRetryTimerRef.current);
          authRetryTimerRef.current = window.setTimeout(() => {
            authRetryTimerRef.current = null;
            void refresh({ background: true });
          }, cooldownMs);
        }
        return serverSessionRef.current;
      }
      console.error("Failed to load auth session.", error);
      // An upstream outage is not a sign-out. Server authorization still
      // checks every protected action; keep the last UI snapshot until retry.
      return serverSessionRef.current;
    } finally {
      lastRefreshAtRef.current = Date.now();
      inFlightRefreshRef.current = null;
      if (options?.forceSubscriptionRefresh) {
        lastForcedRefreshAtRef.current = Date.now();
        inFlightForcedRefreshRef.current = null;
      }
      setLoading(false);
    }
    };

    const promise = Promise.resolve().then(run);
    inFlightRefreshRef.current = promise;
    if (options?.forceSubscriptionRefresh) {
      inFlightForcedRefreshRef.current = promise;
    }
    return promise;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;
    let subscription: ReturnType<typeof supabase.auth.onAuthStateChange>["data"]["subscription"] | null = null;

    void refresh({ forceSubscriptionRefresh: true }).finally(() => {
      if (cancelled) return;

      const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
        if (event === "INITIAL_SESSION" && (session?.user?.id ?? null) === (serverSessionRef.current?.user?.id ?? null)) return;
        if (event === "SIGNED_IN" && session?.user?.id === serverSessionRef.current?.user?.id) return;
        if (event === "SIGNED_OUT" || (event === "SIGNED_IN" && session?.user?.id !== serverSessionRef.current?.user?.id)) authRetryAtRef.current = 0;
        // Never await backend work while Supabase holds its auth lock.
        void refresh({ background: event === "TOKEN_REFRESHED" });
      });
      subscription = data.subscription;
    });

    return () => {
      cancelled = true;
      mountedRef.current = false;
      if (authRetryTimerRef.current !== null) window.clearTimeout(authRetryTimerRef.current);
      authRetryTimerRef.current = null;
      subscription?.unsubscribe();
    };
  }, [refresh]);

  // L2 presence heartbeat (account-sharing OBSERVE pipeline). Keeps this device's
  // session marked "active" so the server-side concurrency MEASUREMENT is accurate.
  // Subscribers only — free users and admins are not measured. Paused while the tab
  // is hidden so a backgrounded device ages out and frees its slot. This is purely
  // measurement: it never signs anyone out.
  const heartbeatEnabled = Boolean(
    serverSession?.authenticated && serverSession?.billing?.personalSubscriptionId,
  );
  useEffect(() => {
    if (!heartbeatEnabled) return;
    const HEARTBEAT_MS = 5 * 60 * 1000;
    const REFRESH_THROTTLE_MS = 15 * 1000;
    let lastVisibilityRefreshAt = 0;
    let lastHeartbeatAt = 0;
    let heartbeatInFlight = false;

    const ping = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      const now = Date.now();
      if (heartbeatInFlight || now < authRetryAtRef.current || (lastHeartbeatAt && now - lastHeartbeatAt < 60000)) return;
      lastHeartbeatAt = now;
      heartbeatInFlight = true;
      void apiFetch("/api/auth/heartbeat", { method: "POST" }).catch(() => {}).finally(() => { heartbeatInFlight = false; });
    };

    ping();
    const timer = window.setInterval(ping, HEARTBEAT_MS);
    const onVisibility = () => {
      if (document.hidden) return;
      ping();
      // Re-fetch the session when the tab regains focus so changes made while
      // away show up — e.g. an upgrade completed in a checkout tab, or a token
      // grant landed. This also drives the server-side entitlement poll (and
      // the plan-change completion sweep). Throttled so rapid tab switches
      // don't spam the endpoint.
      const now = Date.now();
      if (now - lastVisibilityRefreshAt >= REFRESH_THROTTLE_MS) {
        lastVisibilityRefreshAt = now;
        void refresh({ background: true });
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [heartbeatEnabled, refresh]);

  const refreshPermissions = useCallback(async () => {
    await refresh();
  }, [refresh]);

  const refreshIpLock = useCallback(async () => {
    await refresh();
  }, [refresh]);

  const authorizeFeature = useCallback(
    async (feature: FeatureKey, options?: { forceRefresh?: boolean }) => {
      const activeSession = options?.forceRefresh ? await refresh() : serverSession;
      return Boolean((activeSession?.permissions ?? defaultPermissions)[feature]);
    },
    [refresh, serverSession],
  );

  const getKnownFeatureAccess = useCallback(
    (feature: FeatureKey) => {
      if (!serverSession?.authenticated) {
        return null;
      }
      return Boolean((serverSession.permissions ?? defaultPermissions)[feature]);
    },
    [serverSession],
  );

  const signOut = useCallback(async () => {
    authRetryAtRef.current = 0;
    await supabase.auth.signOut();
    await refresh();
  }, [refresh]);

  const value = useMemo<AuthContextValue>(() => {
    const user = serverSession?.authenticated ? serverSession.user : null;
    const permissions = serverSession?.permissions ?? defaultPermissions;

    return {
      user,
      session: user ? { user } : null,
      loading,
      role: serverSession?.role ?? null,
      isAdmin: Boolean(serverSession?.isAdmin),
      canChangePlan: Boolean(serverSession?.canChangePlan),
      billing: serverSession?.billing ?? defaultBilling,
      permissions,
      tokens: serverSession?.tokens ?? 0,
      displayTokens: serverSession?.displayTokens ?? serverSession?.tokens ?? 0,
      ipLock: serverSession?.ipLock ?? defaultIpLock,
      sharingSuspended: Boolean(serverSession?.sharingSuspended),
      sharingSuspendedReason: serverSession?.sharingSuspendedReason ?? null,
      refreshPermissions,
      refreshIpLock,
      authorizeFeature,
      getKnownFeatureAccess,
      signOut,
    };
  }, [
    authorizeFeature,
    getKnownFeatureAccess,
    loading,
    refreshIpLock,
    refreshPermissions,
    serverSession,
    signOut,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return ctx;
}
